/**
 * Retries a function until it succeeds or the maximum number of retries is reached.
 * Supports exponential backoff with jitter for more effective retries.
 *
 * @param fn Function to retry
 * @param options Retry options
 * @returns The result of the function or throws the last error
 */
export const retry = async <T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    baseDelay?: number;
    exponential?: boolean;
    onRetry?: (error: any, attempt: number) => void;
  } = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    baseDelay = 100,
    exponential = false,
    onRetry
  } = options;

  let attempt = 0;

  while (true) {
    try {
      return await fn();
    } catch (error) {
      attempt++;

      if (attempt <= maxRetries) {
        // Calculate delay with exponential backoff and jitter if enabled
        let retryDelay = baseDelay;
        if (exponential) {
          retryDelay = baseDelay * Math.pow(2, attempt - 1);
          // Add jitter (±25% randomization)
          retryDelay = retryDelay * (0.75 + Math.random() * 0.5);
        }

        // Optional callback for logging or metrics
        if (onRetry) {
          onRetry(error, attempt);
        }

        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }

      throw error;
    }
  }
};
